import { Component, OnInit, Input, OnDestroy, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { SummaryModuleDataApiModel, WVBase, WVMain } from '../wallboard-view.model';
import { SummarySettingsTransferObj, ModuleType } from '../../wallboards.model';
import { PageBoundary } from './wallboard-view-summary.models';

@Component({
    selector: 'cv-wallboard-view-summary',
    templateUrl: './wallboard-view-summary.component.html',
    styleUrls: ['./wallboard-view-summary.component.scss']
})
export class WallboardViewSummaryComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() firstSlide;

    @ViewChildren('metricNameRefs') metricNameRefs: QueryList<ElementRef>;
    @ViewChildren('groupNameRefs') groupNameRefs: QueryList<ElementRef>;

    public wvf: WVMain;
    public wvsf: WVBase;
    public summaryState: SummarySettingsTransferObj;
    public summaryData: SummaryModuleDataApiModel;
    public summaryMetricNames: string[] = [];

    public isSmallScreen: boolean = false;
    public index: number = 0;

    // The current data rows visible on the current page.
    public currentPageItems: any[] = [];

    // Pagination
    public currentPage: number = 0;
    public pageSize: number = 0;
    public totalPages: number = 0;
    public totals: any = [];
    public calculatedInterval: number = 0;
    public slideIntervalTime: number;

    private readonly windowHeight = window.innerHeight;
    private tableHeight: number = 0;
    private $slideContainer: JQuery<HTMLElement>;

    private updateTimerRef: NodeJS.Timeout = null;
    private isDestroyed: boolean = false;

    // Precomputed page boundaries based on variable row heights.
    private pages: PageBoundary[] = [];

    // Reusable canvas context for text-width measurement.
    private _measureCtx: CanvasRenderingContext2D | null = null;
    private _measureFont: string = '';
    private _spaceWidth: number = 0;

    private _hasBackdrop: boolean = false;
    private _hasTotalsRow: boolean = false;
    private _hasPagination: boolean = false;
    private _moduleKey: string = "";
    private _backgroundID: number = 0;
    private _wallboardID: number = 0;
    private _nextSlideID: number = 0;

    private readonly _secondInMs = 1000;
    private readonly firstSlideOffsetSeconds = 0.5;
    private readonly subsequentSlideOffsetSeconds = 1.3;
    private readonly lastSlideOffsetSeconds = 1;

    private readonly _paginationMaxRows = 500;
    private readonly _maxMetricColumns = 10;
    private readonly _maxSmallWindowHeight = 720;
    private readonly _smallScreenRowSz = 40;
    private readonly _rowSz = 47;
    private readonly _defaultFontFamily = "'Lato-Regular', sans-serif";
    private readonly _smallScreenFontSz = 20;
    private readonly _fontSz = 24;
    private readonly _tableWidth = 0.88;
    private readonly _lineHeightMultiplier = 1.4;
    private readonly _maxLineHeightMultiplier = 2;
    private readonly _groupNameMaxLineHeightMultiplier = 3;
    private readonly _bootstrapSingleCol = 1 / 12;
    private readonly _bootstrapNameCol = 2 / 12;

    get hasBackdrop(): boolean {
        return this._hasBackdrop;
    }

    get hasTotalsRow(): boolean {
        return this._hasTotalsRow;
    }

    get hasPagination(): boolean {
        return this._hasPagination;
    }

    get moduleKey(): string {
        return this._moduleKey;
    }

    get backgroundID(): number {
        return this._backgroundID;
    }

    private get showTop(): number {
        if (this.hasPagination) {
            return this._paginationMaxRows;
        }
        return Math.floor(this.tableHeight / this.rowHeight);
    }

    private get rowHeight(): number {
        return this.isSmallScreen ? this._smallScreenRowSz : this._rowSz;
    }

    private get fontSize(): number {
        return this.isSmallScreen ? this._smallScreenFontSz : this._fontSz;
    }

    constructor(
        private readonly service: WallboardViewService
    ) { }

    ngOnInit(): void {
        const verticalSpaceOutsideTable = 132;
        this.wvf = this.service.WVMain;
        this.wvsf = this.service.WVSummary;

        this._hasBackdrop = this.wvsf?.Slide.HasBackdrop ?? false;
        this._hasTotalsRow = this.wvsf?.Slide.HasTotalsRow ?? false;
        this._hasPagination = this.wvsf?.Slide.HasPagination ?? false;
        this._moduleKey = this.wvsf?.Slide.WallboardModuleDetails[0].WallboardModuleKey ?? "";
        this._backgroundID = this.wvsf?.Slide.BackgroundImageID ?? 0;
        this._wallboardID = this.wvsf?.Slide.WallboardID ?? 0;
        this._nextSlideID = this.wvsf?.Slide.WallboardModuleDetails[0].WallboardSlideID ?? 0;

        if (this.windowHeight <= this._maxSmallWindowHeight) {
            this.isSmallScreen = true;
        }

        this.wvf.RunSlide = this.firstSlide;

        this.$slideContainer = angular.element(document).find('.wallboardViewSlide');
        this.$slideContainer.css("height", "100vh");

        this.tableHeight = this.$slideContainer.find(".wallboardSummarySlideContentArea").height()
            - verticalSpaceOutsideTable;

        this.initMeasureContext();

        let moduleState = this.service.loadModuleState(ModuleType.Summary, this.wvf.WallboardCreatorID, this.moduleKey)
            .then(response => {
                if (this.isDestroyed) {
                    return;
                }

                this.summaryState = response.sumObj;
            });

        let summaryModuleData = this.service.getSummaryModuleData(this._wallboardID, this.showTop, this.hasTotalsRow,
            this.hasPagination, this.moduleKey).then(response => {
                if (this.isDestroyed) {
                    return;
                }

                this.summaryData = response;
            });

        this.service.getSummaryMetricNames(this._wallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed) {
                return;
            }

            this.summaryMetricNames = response;
        });

        Promise.all([moduleState, summaryModuleData]).then(responses => {
            const verifyNextSlide = !this.firstSlide && (this._nextSlideID === this.wvf.Slides[this.wvf.NextSlide]
                .WallboardSlideID);

            if (this.firstSlide) {
                this.slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval
                    - this.firstSlideOffsetSeconds) * this._secondInMs;
            }
            else if (this.wvf.LastSlide) {
                this.slideIntervalTime = (this.wvf.LastSlide.SlideInterval - this.lastSlideOffsetSeconds)
                    * this._secondInMs;
            }
            else {
                this.slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval
                    - this.subsequentSlideOffsetSeconds) * this._secondInMs;
            }

            this.preprocessGroupNames();

            if (this.hasTotalsRow) {
                this.totals = this.summaryData.Summary_Module_Data_List.pop();
            }

            // Compute page boundaries with variable row heights then update currentPages
            this.computePageLayout();
            this.updateCurrentPageItems();

            if (this.hasPagination) {
                if (this.totalPages > 1) {
                    this.calculatedInterval = ((this.slideIntervalTime - this._secondInMs) / this.totalPages);
                    const paginationValues = this.service.calculatePagination(this.calculatedInterval,
                        this.slideIntervalTime, this.totalPages, verifyNextSlide);
                    this.slideIntervalTime = paginationValues[0];
                    this.calculatedInterval = paginationValues[1];
                }
                else {
                    this.currentPage = 0;
                }
            }

            if (this.firstSlide) {
                this.wvf.FirstSlideReady = true;
            }
            else {
                this.wvf.NextSlideReady = true;
            }

            this.runSlide();
        });
    }

    ngAfterViewInit(): void {
        this.updateTextClamps();
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null) {
            clearInterval(this.updateTimerRef);
        }

        this.isDestroyed = true;
        this._measureCtx = null;
    }

    getGlobalRowIndex(localIndex: number): number {
        if (this.pages.length === 0 || this.currentPage >= this.pages.length) {
            return localIndex;
        }
        return this.pages[this.currentPage].startIndex + localIndex;
    }

    async runSlide() {
        if (this.isDestroyed) {
            return;
        }

        if (!this.updateTimerRef) {
            const refreshInterval = this.summaryData.Has_Current_Metric
                ? WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL
                : WallboardViewService.HISTORICAL_DATA_REFRESH_INTERVAL;

            this.updateTimerRef = setInterval(() => this.updateSlideData(), refreshInterval);
        }

        if (this.hasPagination && this.wvf.RunSlide) {
            setTimeout(() => {
                if (this.calculatedInterval != null && this.totalPages > 0) {
                    for (let i = 0; i < this.totalPages; i++) {
                        setTimeout(() => {
                            this.currentPage = this.service.runPagination(this.index, this.currentPage);
                            this.index++;
                            this.updateCurrentPageItems();
                        }, i * this.calculatedInterval, true, i);
                    }
                }
            });
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, this._secondInMs);
        }
    }

    async updateSlideData() {
        const response = await this.service.getSummaryModuleData(this._wallboardID, this.showTop, this.hasTotalsRow,
            this.hasPagination, this.moduleKey);

        if (this.isDestroyed) {
            return;
        }

        this.summaryData = response;

        this.preprocessGroupNames();

        if (this.hasTotalsRow) {
            this.totals = response.Summary_Module_Data_List.pop();
        }

        // Recompute page boundaries with the updated data
        this.computePageLayout();

        // Ensure the current page is still valid after recomputation
        if (this.currentPage >= this.pages.length) {
            this.currentPage = Math.max(this.pages.length - 1, 0);
        }

        this.updateCurrentPageItems();
    }

    updateTextClamps(): void {
        if (!this.metricNameRefs || !this.groupNameRefs) {
            return;
        }
        // Log the text content of each referenced element
        this.metricNameRefs.forEach((elementRef, index) => {
            const textContent = elementRef.nativeElement.textContent;
            // make text fit within two lines, truncating with ellipsis if necessary, but just dividing the word if
            //  possible with spaces. For instance, 30px for each line, so 60px for two lines
            const lineHeight = Number.parseInt(globalThis.getComputedStyle(elementRef.nativeElement).lineHeight, 10);
            const maxHeight = lineHeight * this._maxLineHeightMultiplier;
            // Check if the content exceeds the maximum height
            if (elementRef.nativeElement.offsetHeight > maxHeight) {
                let originalText = textContent;
                while (elementRef.nativeElement.offsetHeight > maxHeight && originalText.length > 0) {
                    originalText = originalText.slice(0, -1);
                    elementRef.nativeElement.textContent = originalText + '...';
                }
            }
        });
        this.groupNameRefs.forEach((elementRef, index) => {
            const textContent = elementRef.nativeElement.textContent;
            // make text fit within three lines, truncating with ellipsis if necessary, but just dividing the word if
            //  possible with spaces
            const lineHeight = Number.parseInt(globalThis.getComputedStyle(elementRef.nativeElement).lineHeight, 10);
            const maxHeight = lineHeight * this._groupNameMaxLineHeightMultiplier;
            // Check if the content exceeds the maximum height
            if (elementRef.nativeElement.offsetHeight > maxHeight) {
                let originalText = textContent;
                while (elementRef.nativeElement.offsetHeight > maxHeight && originalText.length > 0) {
                    originalText = originalText.slice(0, -1);
                    elementRef.nativeElement.textContent = originalText + '...';
                }
            }
        });
    }

    /**
     * Returns the class names for the main content area.
     */
    getMainClasses() {
        return `summaryViewMetricArea${this.hasTotalsRow ? "Totals" : ""}${this.hasBackdrop ? " summaryViewDark" : ""}`
            + `${this.isSmallScreen ? " smallScreen" : ""}`;
    }

    /**
     * Returns the number of columns that the empty space should span.
     */
    getColumnWidth() {
        // Number of metrics, with enforced minimum of 0 and maximum of Max-1 enforced
        return Math.max(Math.min(this._maxMetricColumns - (this.summaryMetricNames?.length ?? 0),
            this._maxMetricColumns - 1), 0);
    }

    private initMeasureContext(): void {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        this._measureCtx = canvas.getContext('2d');

        const slideEl = this.$slideContainer?.[0] || document.body;
        const fontFamily = getComputedStyle(slideEl).fontFamily || this._defaultFontFamily;

        this._measureFont = `${this.fontSize}px ${fontFamily}`;
        this._measureCtx.font = this._measureFont;
        this._spaceWidth = this._measureCtx.measureText(' ').width;
    }

    private preprocessGroupNames() {
        this.summaryData?.Summary_Module_Data_List?.forEach(item => {
            if (item.groupName) {
                item.groupName = this.addBreakHints(item.groupName);
            }
        });
    }

    /**
     * Trims the name and adds break hints.
     * '-' is replaced by a non-breaking variant and the breakhint is added behind it.
     * '_' is replaced with an underscore followed by the breakhint.
     */
    private addBreakHints(name: string): string {
        return name ? name.trim().replaceAll('-', '\u2011\u200B').replaceAll('_', '_\u200B') : "";
    }

    private getNameColumnTextWidth(ctx: CanvasRenderingContext2D, dataLength: number): number {
        const containerContentWidth = window.innerWidth * this._tableWidth;
        const nameColWidth = containerContentWidth * this._bootstrapNameCol;
        const bootstrapColPadding = 30;
        const rankBuffer = 12;

        let rankWidth = 0;
        if (this.summaryState?.ShowRank) {
            const maxRankStr = dataLength.toString();
            rankWidth = ctx.measureText(maxRankStr).width + rankBuffer;
        }

        return Math.max(nameColWidth - bootstrapColPadding - rankWidth, 0);
    }

    private doesNameWrap(name: string, maxWidthPx: number, ctx: CanvasRenderingContext2D): boolean {
        const trimmed = name?.trim() || '';
        if (!trimmed) {
            return false;
        }

        if (ctx.measureText(trimmed).width <= maxWidthPx) {
            return false;
        }

        // Simulate word wrapping at spaces and no-width spaces
        const words = trimmed.split(/\s+|\u200B/);
        let currentLineWidth = 0;

        for (const element of words) {
            const wordWidth = ctx.measureText(element).width;
            const testWidth = currentLineWidth > 0
                ? currentLineWidth + this._spaceWidth + wordWidth
                : wordWidth;

            if (testWidth > maxWidthPx && currentLineWidth > 0) {
                return true;
            }

            currentLineWidth = testWidth;
        }

        return false;
    }

    private computePageLayout(): void {
        const data = this.summaryData?.Summary_Module_Data_List;
        const ctx = this._measureCtx;
        if (!data || !ctx || data.length === 0) {
            this.pages = [];
            this.totalPages = 0;
            this.pageSize = 0;
            return;
        }

        ctx.font = this._measureFont;

        const pages = this.buildPageBoundaries(data, ctx)
        this.applyPages(pages);
    }

    private buildPageBoundaries(data: any[], ctx: CanvasRenderingContext2D): PageBoundary[] {
        const nameTextWidth = this.getNameColumnTextWidth(ctx, data.length);

        // Row height constants.
        const singleRowHeight = this.rowHeight;
        const fontSize = this.fontSize;
        const doubleRowHeight = singleRowHeight + Math.ceil(fontSize * this._lineHeightMultiplier);
        const firstRowExtraHeight = 10;

        const availableHeight = this.tableHeight;

        const pages: PageBoundary[] = [];
        const headerHeight = this.computeHeaderHeight(ctx);

        let accumulatedHeight = headerHeight + firstRowExtraHeight;
        let pageStartIndex = 0;

        for (let i = 0; i < data.length; i++) {
            const rowHeight = this.doesNameWrap(data[i].groupName, nameTextWidth, ctx)
                ? doubleRowHeight
                : singleRowHeight;

            if (accumulatedHeight + rowHeight > availableHeight && i > pageStartIndex) {
                pages.push({ startIndex: pageStartIndex, count: i - pageStartIndex });
                pageStartIndex = i;
                accumulatedHeight = rowHeight + headerHeight + firstRowExtraHeight;
            }
            else {
                accumulatedHeight += rowHeight;
            }
        }

        if (pageStartIndex < data.length) {
            pages.push({ startIndex: pageStartIndex, count: data.length - pageStartIndex });
        }

        return pages;
    }

    private applyPages(pages: PageBoundary[]) {
        this.pages = pages;

        if (this.hasPagination) {
            this.totalPages = pages.length;
        }
        else {
            this.totalPages = 0;
            if (pages.length > 1) {
                this.pages = [pages[0]];
            }
        }

        this.pageSize = pages.length > 0 ? pages[0].count : 0;
    }

    private computeHeaderHeight(ctx: CanvasRenderingContext2D): number {
        const fontSize = this.fontSize;
        const lineHeight = Math.ceil(fontSize * this._lineHeightMultiplier);
        const metricLeftPadding = 15;
        const headerTopPadding = 10;

        const containerContentWidth = window.innerWidth * this._tableWidth;
        const metricColWidth = containerContentWidth * this._bootstrapSingleCol;
        const metricTextWidth = Math.max(metricColWidth - metricLeftPadding, 0);

        // If ANY visible metric name wraps, the entire header row is 2-line height
        const anyMetricWraps = this.summaryMetricNames?.slice(0, this._maxMetricColumns)
            .some(name => this.doesNameWrap(name, metricTextWidth, ctx)) ?? false;

        return (anyMetricWraps ? lineHeight * this._maxLineHeightMultiplier : lineHeight) + headerTopPadding;
    }

    private updateCurrentPageItems(): void {
        if (!this.summaryData?.Summary_Module_Data_List || this.pages.length === 0) {
            this.currentPageItems = [];
            return;
        }

        const pageIndex = Math.min(this.currentPage, this.pages.length - 1);
        const page = this.pages[pageIndex];
        this.currentPageItems = page
            ? this.summaryData.Summary_Module_Data_List.slice(page.startIndex, page.startIndex + page.count)
            : [];
    }
}
